import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  TagCategory,
  getTagCategories,
  createTagCategory,
  updateTagCategory,
  deleteTagCategory
} from '@/services/supabase/tag-categories';

// Query keys
export const tagCategoryKeys = {
  all: ['tagCategories'] as const,
  lists: () => [...tagCategoryKeys.all, 'list'] as const,
  list: (filters: string) => [...tagCategoryKeys.lists(), { filters }] as const,
  details: () => [...tagCategoryKeys.all, 'detail'] as const,
  detail: (id: string) => [...tagCategoryKeys.details(), id] as const,
};

// Stale and garbage collection times
const STALE_TIME = 1000 * 60 * 5; // 5 minutes
const GC_TIME = 1000 * 60 * 10; // 10 minutes

/**
 * Hook to get all tag categories for a project
 */
export function useTagCategories(projectId: string | number | undefined) {
  return useQuery({
    queryKey: tagCategoryKeys.list(projectId?.toString() || ''),
    queryFn: () => getTagCategories(projectId || ''),
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    enabled: !!projectId,
  });
}

/**
 * Hook to create a tag category
 */
export function useCreateTagCategory() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (category: {
      name: string;
      description?: string;
      color?: string;
      project_id: number;
    }) => createTagCategory(category),
    onSuccess: (_, variables) => {
      // Invalidate the project tag categories query to force a refetch
      queryClient.invalidateQueries({
        queryKey: tagCategoryKeys.list(variables.project_id.toString()),
      });
    },
  });
}

/**
 * Hook to update a tag category
 */
export function useUpdateTagCategory() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      categoryId,
      updates,
      projectId,
    }: {
      categoryId: number;
      updates: {
        name?: string;
        description?: string | null;
        color?: string;
      };
      projectId: string | number;
    }) => updateTagCategory(categoryId, updates),
    onSuccess: (_, variables) => {
      // Invalidate the project tag categories query to force a refetch
      queryClient.invalidateQueries({
        queryKey: tagCategoryKeys.list(variables.projectId.toString()),
      });
      // Invalidate the specific tag category detail
      queryClient.invalidateQueries({
        queryKey: tagCategoryKeys.detail(variables.categoryId.toString()),
      });
    },
  });
}

/**
 * Hook to delete a tag category
 */
export function useDeleteTagCategory() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      categoryId,
      projectId,
    }: {
      categoryId: number;
      projectId: string | number;
    }) => deleteTagCategory(categoryId),
    onSuccess: (_, variables) => {
      // Invalidate the project tag categories query to force a refetch
      queryClient.invalidateQueries({
        queryKey: tagCategoryKeys.list(variables.projectId.toString()),
      });
    },
  });
}
